import React from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import CleaningZone from "../../../../interfaces/robot/rooms/cleaning-zone";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import { selectIsAdvancedModeEnabled } from "../../../../store/app-configurations/app-configurations-selectors";
import { NavigatorAvailability, NavigatorType } from "../../../../enums/command-hub/navigator";
import { navigators } from "../../../../interfaces/robot/navigator";
import NavigatorSelection, { NavigatorChangeProps } from "../../Selectors/NavigatorSelection";
import { getPolygonAreaString } from "../../../../utils/map/zone-helper";
import CleaningSettingsForm from "./CleaningSettingsForm";
import useDistanceUnitValue from "../../../../hooks/useDistanceUnitValue";
import { DistanceValueUnit } from "../../../../enums/configuration/units";

const SelectedZoneFormCleaningZoneDetails: React.FC<{
  zone: CleaningZone;
}> = ({ zone }) => {
  const dispatch = useSharedDispatch();

  const { patchCleaningZone } = useSharedActions();

  const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));

  const { multiplier, label: distanceUnitLabel } = useDistanceUnitValue(0, DistanceValueUnit.METERS_SQUARED);

  const { t } = useTranslation();

  const navigatorChangeHandler = (payload: Partial<NavigatorChangeProps>) => {
    dispatch(
      patchCleaningZone({
        id: zone.id,
        data: { cleaningZone: { navigator: payload.navigatorName, doWallFollow: payload.doWallFollow } },
      }),
    );
  };

  const filteredAvailableNavigators = isAdvancedModeEnabled
    ? navigators.filter((n) => n.type === NavigatorType.CleaningNavigator)
    : navigators.filter(
        (n) => n.availability === NavigatorAvailability.Public && n.type === NavigatorType.CleaningNavigator,
      );

  if (zone.navigator != null && !filteredAvailableNavigators.some((n) => n.name === zone.navigator)) {
    const selectedNavigator = navigators.find((n) => n.name === zone.navigator);
    if (selectedNavigator != null) {
      filteredAvailableNavigators.push(selectedNavigator);
    }
  }

  return (
    <React.Fragment>
      <NavigatorSelection
        navigatorName={zone.navigator ?? ""}
        navigators={filteredAvailableNavigators}
        onChange={navigatorChangeHandler}
        doWallFollow={zone.doWallFollow}
      />

      <Grid item xs={12}>
        <Typography>
          {`${t("zone-area")}: ${getPolygonAreaString(
            zone.points.map((p) => p.point),
            multiplier,
          )}${distanceUnitLabel}`}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <CleaningSettingsForm zone={zone} />
      </Grid>
    </React.Fragment>
  );
};

export default SelectedZoneFormCleaningZoneDetails;
